// vendors
import isEmpty from 'lodash/isEmpty';

// misc
import { getModelPrivacy } from '@v4/services/api/v3/offer';
import { getApiRootUrl } from '@v4/config';
import queryString from 'query-string';
import {
    windowHistoryReplaceStateParams,
    getQueryParams,
    stringifyQueryParams,
    stringifyUrl,
} from '@v4/common/utils/url';
import { getSubmodelFilterVersions } from '@v4/services/api';
import { removeClass } from '@v4/common/utils/dom';

// components
import VersionsHandler from '@v4/multi-section/components/organisms/VersionsHandler';
import LoadingWrapper from '@v4/common/components/atoms/LoadingWrapper.vue';

// mixins
import { dkModalAware } from '@v4/common/components/mixins/modal';
import { modalWizardCarConfigurator, modalWizardLeadForm } from '@v4/common/components/mixins/modal-service';
import gaEventHandler from '@v4/common/components/mixins/google-analytics';
import clickoutPopupEventsHandler from '@v4/common/components/mixins/clickout-popup-events-handler';
import clickToCallModalHandler from '@v4/common/components/mixins/click-to-call-modal-handler';

// styles
import '@v4/multi-section/mixins/versions/_index.scss';
import { EventBus } from '@v4/common/utils/event-bus';

export default () => ({
    name: 'VehicleVersionsPage',
    data() {
        return {
            selectedOffer: {},
            additionalQueryParams: {
                // eslint-disable-next-line camelcase
                expanded_lists: getQueryParams().expanded_lists || '',
            },
            isBusy: false,
            tempSelectedVersionId: null,
        };
    },
    components: {
        VersionsHandler,
        LoadingWrapper,
        Accordion: () => ({
            component: import(/* webpackChunkName: "Accordion-vue" */ '@v4/common/components/molecules/Accordion'),
        }),
        ClickoutBox: () => ({
            component: import(/* webpackChunkName: "ClickoutBox-vue" */ '@v4/common/components/molecules/ClickoutBox'),
        }),
        DkNewsCard: () => ({
            component: import(
                /* webpackChunkName: "DkNewsCard-vue" */ '@v4/common/components/organisms/DkNewsCard.vue'
            ),
        }),
    },
    mixins: [
        dkModalAware,
        modalWizardCarConfigurator,
        gaEventHandler,
        clickoutPopupEventsHandler,
        modalWizardLeadForm,
        clickToCallModalHandler,
    ],
    mounted() {
        const skeletonElm = this.$el.querySelector('.js-multi-section-versions.skeleton');

        if (skeletonElm) {
            removeClass(skeletonElm, 'skeleton');
        }

        if (this.fetchedData && this.fetchedData.events && this.fetchedData.events['view_item_list']) {
            this.pushGtagEvent('view_item_list', null, this.fetchedData.events);
        }

        EventBus.$on('LeadForm:input', this.onLeadFormInput);

        const makeModel = `${this.fetchedData.makeName} ${this.fetchedData.submodelCommercialName}`;
        this.bindPopupClickoutTracking('Vehicle versions page', makeModel);
    },
    beforeDestroy() {
        EventBus.$off();
    },
    computed: {
        versions() {
            return (this.fetchedData && this.fetchedData.versions) || [];
        },
        filters() {
            return (this.fetchedData && this.fetchedData.facetList) || [];
        },
        theApiResource() {
            const { makeUrlCode, submodelCommercialUrlCode } = this.fetchedData;
            return `/wp-json/dkwp/v2/car/${makeUrlCode}/${submodelCommercialUrlCode}/filter-versions-by`;
        },
        versionsCount() {
            if (!this.versions.length) {
                return 0;
            }

            return this.versions.reduce((accumulator, currentItem) => {
                const { items } = currentItem;
                if (items && items.length) {
                    return accumulator + currentItem.items.length;
                }
            }, 0);
        },
        checkedFilters() {
            const checkedFilters = this.filters.filter((group) => group.id === 'filters')[0];
            return (checkedFilters && checkedFilters.items && checkedFilters.items.length) || 0;
        },
        showClickoutBox() {
            return this.fetchedData.clickout_section && !isEmpty(this.fetchedData.clickout_section);
        },
        hasWizardForm() {
            return !!getQueryParams().feature_toggle_form_wizard;
        },
    },
    methods: {
        async setSelectedOffer(offer, versionName) {
            if (!isEmpty(this.offer)) {
                this.pushGaEvent('versions-list', 'replaced', null, null, {
                    old: this.offer.versionId,
                    new: offer.versionId,
                });
            }

            // Offer is deselected
            if (
                !isEmpty(this.selectedOffer) &&
                (this.selectedOffer.versionId === offer.versionId && this.selectedOffer.offerId === offer.offerId)
            ) {
                this.pushGaEvent('versions-list', 'deselected', null, this.selectedOffer.versionId || '');
            } else {
                // New offer is selected
                this.pushGaEvent('versions-list', 'selected', null, offer.versionId);
            }

            this.selectedOffer = offer;

            if (!this.hasWizardForm) {
                this.showWizardCarConfigurator(versionName);
            }
        },
        async handleLeadFormHiddenFields(selectedOffer = null, leadFormWrapperRef) {
            const item = this.selectedOffer || selectedOffer || {};
            const leadFormWrapper = leadFormWrapperRef || this.$refs.leadFormWrapper || {};
            const { projectId, poId } = item;

            if (poId) {
                leadFormWrapper.setLeadFormHiddenField('dkwp[poId]', poId);
            }

            if (projectId) {
                leadFormWrapper.setLeadFormHiddenField('dkwp[projectID]', projectId);
                try {
                    const data = await getModelPrivacy(projectId);
                    const { text, privacies } = data.data;

                    leadFormWrapper.setLeadFormPrivacyFlags(privacies);
                    leadFormWrapper.updateLeadFormCtasPrivacyPolicyText(text);
                } catch (error) {
                    leadFormWrapper.setLeadFormPrivacyFlags(null);
                    leadFormWrapper.updateLeadFormCtasPrivacyPolicyText(null);
                }
            }
        },
        showWizardCarConfigurator(versionName) {
            if (!this.fetchData) {
                return;
            }
            const {
                makeUrlCode,
                submodelCommercialUrlCode,
                formId,
                formData,
                makeName,
                submodelCommercialName,
                toast,
            } = this.fetchedData;

            this.clearClickToCallTimer();
            this.openWizardCarConfiguratorModal({
                make: makeUrlCode,
                model: submodelCommercialUrlCode,
                leadFormWrapper: {
                    formId,
                    formData,
                },
                leadFormMeta: {
                    title: `${makeName} ${submodelCommercialName}`,
                    subtitle: versionName || '',
                    badgeText: this.selectedOffer.label || '',
                },
                leadFormIncentive: toast,
                handleLeadFormHiddenFields: this.handleLeadFormHiddenFields,
                onDiscoverPrice: () =>
                    this.pushGtagEvent('add_to_cart', this.tempSelectedVersionId, this.fetchedData.events),
            });
        },
        scrollTop() {
            document.body.scrollIntoView({ behavior: 'smooth' });
        },
        getRequestUrlByHref(href) {
            let url = '';
            let query = {};

            if (href) {
                const parsedUrl = queryString.parseUrl(href);

                query = { ...parsedUrl.query, ...this.additionalQueryParams };

                // Remove empty query params
                Object.keys(this.additionalQueryParams).forEach((key) => {
                    if (Object.prototype.hasOwnProperty.call(query, key) && !query[key]) {
                        Reflect.deleteProperty(query, key);
                    }
                });

                url = stringifyUrl({ url: `${getApiRootUrl()}${parsedUrl.url}`, query });
            }

            return { url, query };
        },
        updateBrowserQueryStringParamAndFetch(key, value) {
            const parsedQueryParams = getQueryParams();

            // Whenever we would like to fetch new data we should ensure
            // that `pag` parameter it's not sent to the request.
            if (key !== 'pag') {
                Reflect.deleteProperty(parsedQueryParams, 'pag');
            }

            const stringifiedQueryParams = stringifyQueryParams({ ...parsedQueryParams, [key]: value });

            const href = `${this.theApiResource}${stringifiedQueryParams}`;

            return this.fetchData(href);
        },
        fetchData(href) {
            if (!href) {
                return;
            }

            this.isBusy = true;

            const { query } = this.getRequestUrlByHref(href);
            const { makeUrlCode, submodelCommercialUrlCode } = this.fetchedData;

            // Update browser's query string
            windowHistoryReplaceStateParams(query);
            return getSubmodelFilterVersions(makeUrlCode, submodelCommercialUrlCode, query)
                .then((response) => response.json())
                .then((data) => {
                    const { versions, facetList, tabs } = data;
                    this.fetchedData = {
                        ...this.fetchedData,
                        versions,
                        facetList,
                        tabs,
                    };
                    this.isBusy = false;

                    return this.fetchedData;
                })
                .catch(() => {
                    this.isBusy = false;
                })
                .finally(() => {
                    this.isBusy = false;
                    this.scrollTop();
                });
        },
        onChipClick({ item }) {
            return this.fetchData(item.href);
        },
        onItemsTogglerClick({ filter, expanded }) {
            const filterId = (filter && filter.id) || null;
            const expandedLists = this.additionalQueryParams.expanded_lists;
            const separator = '|';

            let expandedListsItems = expandedLists && expandedLists.length ? expandedLists.split(separator) : [];

            if (!expanded) {
                expandedListsItems = expandedListsItems.filter((id) => id !== filterId);
            } else if (!expandedListsItems.includes(filterId)) {
                expandedListsItems.push(filterId);
            }

            this.$set(this.additionalQueryParams, 'expanded_lists', expandedListsItems.join(separator));
        },
        onClearFiltersClick() {
            // Let's keep `sort` query parameter if exists
            return this.fetchData(`${this.theApiResource}${stringifyQueryParams({ sort: getQueryParams().sort })}`);
        },
        onCheckboxClick(e) {
            const url = e.target.value;
            return this.fetchData(url);
        },
        onRangeSliderUpdate(slider, { id }) {
            const min = slider[2][0];
            const max = slider[2][1];
            return this.updateBrowserQueryStringParamAndFetch(id, `${min},${max}`);
        },
        onPromotionSwitcherChange(payload) {
            const { href } = payload;

            return this.fetchData(href);
        },
        onSearchFiltersClick() {
            return import(
                /* webpackChunkName: "SearchFiltersModal-vue" */ '@v4/search/components/organisms/SearchFiltersModal/SearchFiltersModal.vue'
            ).then(({ default: SearchFiltersModal }) => {
                this.showModal(
                    SearchFiltersModal,
                    {
                        filters: this.filters,
                        handleRangeSliderUpdate: this.onRangeSliderUpdate,
                        handlePromotionSwitcherChange: this.onPromotionSwitcherChange,
                        handleCheckboxClick: this.onCheckboxClick,
                        handleClearFiltersClick: this.onClearFiltersClick,
                        handleItemsTogglerClick: this.onItemsTogglerClick,
                        handleChipClick: this.onChipClick,
                    },
                    {
                        name: 'versions-filters-modal',
                        height: 'auto',
                        classes: 'multi-section-versions__filters-modal modal--fullscreen_medium',
                    }
                );
            });
        },
        onSelectVersion(versionId) {
            this.tempSelectedVersionId = versionId;
            // this.pushGtagEvent('add_to_cart', versionId, this.fetchedData.events);
        },
        onLeadFormInput() {
            this.pushGtagEvent('begin_checkout', this.tempSelectedVersionId, this.fetchedData.events);
        },
        onGuideClick(id, title, category) {
            this.pushGtagEvent('select_content', null, null, {
                // eslint-disable-next-line camelcase
                content_type: category,
                // eslint-disable-next-line camelcase
                item_id: id,
                // eslint-disable-next-line camelcase
                title_guide: title,
                // eslint-disable-next-line camelcase
                page_source: window.location.href,
            });
        },
        openWizardFormModal(version) {
            const {
                formData,
                formId,
                fullName,
                makeUrlCode: make,
                submodelCommercialUrlCode: model,
            } = this.fetchedData;

            const { name: versionName } = version;

            const additionalProps = this.getAdditionalProps(version);

            const props = {
                formId,
                formData,
                fullName,
                make,
                model,
                versionName,
                ...additionalProps,
            };

            this.clearClickToCallTimer();
            this.openWizardLeadFormModal(props, this.setSelectedOffer);
        },
        getAdditionalProps(version) {
            if (version.offers) {
                return { offers: version.offers };
            } else if (version.offer) {
                const { type: offerType, finalPrice: price } = version.offer;
                return { offerType, price };
            }
            return {};
        },
    },
});
